import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildTradingAccountPayloadFromForm,
  mapCurrenciesResponseToDto,
  mapTradingAccountItemToDto,
  mapPlatformsResponseToDto,
  mapTradingAccountFieldErrors,
  mapTradingAccountsResponseToDto,
  mapTradingServersResponseToDto,
  validateTradingAccountPayload,
} from '../dto/system-trading-accounts.dto.js';
import {
  buildEmptyTradingAccountForm,
  buildServerLabelMap,
  buildTradingAccountsRowsModel,
} from '../model/system-trading-accounts.model.js';

export function useSystemTradingAccounts() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [tradingServerOptions, setTradingServerOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsTargetId, setDetailsTargetId] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [form, setForm] = useState(buildEmptyTradingAccountForm(''));
  const [formErrors, setFormErrors] = useState({});
  const [modalError, setModalError] = useState('');
  const [leverageInput, setLeverageInput] = useState('');

  const detailsRow = useMemo(
    () => rows.find((row) => row?.id === detailsTargetId) || null,
    [rows, detailsTargetId],
  );

  const serverLabelById = useMemo(
    () => buildServerLabelMap(tradingServerOptions),
    [tradingServerOptions],
  );

  const loadDictionaries = useCallback(async () => {
    try {
      const [platformsResponse, currenciesResponse] = await Promise.all([
        adminApi.getPlatformsDictionary(token),
        adminApi.getCurrenciesDictionary(token),
      ]);
      const tradingServersResponse = await adminApi.getTradingServers(token);

      const nextPlatforms = mapPlatformsResponseToDto(platformsResponse);
      const nextCurrencies = mapCurrenciesResponseToDto(currenciesResponse);
      const nextTradingServers = mapTradingServersResponseToDto(tradingServersResponse);

      setPlatformOptions(nextPlatforms);
      setCurrencyOptions(nextCurrencies);
      setTradingServerOptions(nextTradingServers);
      setForm((prev) => ({
        ...prev,
        platform: prev.platform || nextPlatforms[0]?.value || '',
      }));
    } catch {
      setPlatformOptions([]);
      setCurrencyOptions([]);
      setTradingServerOptions([]);
    }
  }, [token]);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getTradingAccounts(token);
      const dto = mapTradingAccountsResponseToDto(response);
      setRows(buildTradingAccountsRowsModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setRows([]);
        setError(requestError.message || 'Failed to load trading accounts');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDictionaries();
    loadAccounts();
  }, [loadAccounts, loadDictionaries]);

  const closeModals = useCallback(() => {
    if (isSaving) {
      return;
    }

    setDetailsTargetId('');
    setIsCreateOpen(false);
    setEditingTargetId('');
    setDeleteTargetId('');
    setFormErrors({});
    setModalError('');
    setLeverageInput('');
  }, [isSaving]);

  const openCreate = useCallback(() => {
    setDetailsTargetId('');
    setEditingTargetId('');
    setDeleteTargetId('');
    setIsCreateOpen(true);
    setForm(buildEmptyTradingAccountForm(platformOptions[0]?.value || '', platformOptions[0]?.capabilities));
    setFormErrors({});
    setModalError('');
    setLeverageInput('');
    setError('');
    setSuccess('');
  }, [platformOptions]);

  const openEdit = useCallback((row) => {
    setDetailsTargetId('');
    setDeleteTargetId('');
    setIsCreateOpen(false);
    setEditingTargetId(row.id);
    setForm({
      platform: row.platform || platformOptions[0]?.value || '',
      capabilities: platformOptions.find(option => option.value === row.platform)?.capabilities ?? {},
      tradingServerId: row.tradingServerId || '',
      name: row.name || '',
      group: row.group || '',
      platformUrl: row.platformUrl || '',
      stopOutPercent: row.stopOutPercent ?? '50.00',
      description: row.description || '',
      type: row.type || 'live',
      leverages: row.leverages.map((value) => String(value)),
      currencies: row.currencies,
      minimumDeposit: row.minimumDeposit === null || row.minimumDeposit === undefined ? '' : String(row.minimumDeposit),
    });
    setFormErrors({});
    setModalError('');
    setLeverageInput('');
    setError('');
    setSuccess('');
  }, [platformOptions]);

  const openDetails = useCallback((id) => {
    setDetailsTargetId(id);
  }, []);

  const requestDelete = useCallback((id) => {
    setDeleteTargetId(id);
  }, []);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => {
      if (key !== 'platform') {
        return { ...prev, [key]: value };
      }

      const nextPlatform = String(value || '').trim();
      const capabilities = platformOptions.find(option => option.value === nextPlatform)?.capabilities ?? {};
      const allowedTypes = capabilities.allowedAccountTypes ?? ['demo', 'live'];
      const allowedCurrencies = capabilities.allowedCurrencies ?? [];
      const next = { ...prev, platform: nextPlatform, capabilities,
        type: allowedTypes.includes(prev.type) ? prev.type : allowedTypes[0],
        currencies: allowedCurrencies.length === 1 ? [...allowedCurrencies] : prev.currencies.filter(value => !allowedCurrencies.length || allowedCurrencies.includes(value)),
        leverages: prev.leverages.filter(value => Number.isInteger(Number(value))
          && (capabilities.minLeverage == null || Number(value) >= capabilities.minLeverage)
          && (capabilities.maxLeverage == null || Number(value) <= capabilities.maxLeverage)),
      };
      const hasMatchingServer = tradingServerOptions.some(
        (server) => server.id === prev.tradingServerId && String(server.platform || '').trim() === nextPlatform,
      );
      if (!hasMatchingServer) {
        next.tradingServerId = '';
      }
      return next;
    });
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      if (key === 'platform') {
        delete next.tradingServerId;
      }
      return next;
    });
  }, [tradingServerOptions, platformOptions]);

  const filteredTradingServers = useMemo(
    () => tradingServerOptions.filter((server) => String(server.platform || '').trim() === String(form.platform || '').trim()),
    [tradingServerOptions, form.platform],
  );

  const leverageTags = useMemo(
    () => (Array.isArray(form.leverages) ? form.leverages : []).map((value) => ({ value: String(value), label: String(value) })),
    [form.leverages],
  );

  const addLeverageTagValue = useCallback((rawValue) => {
    const value = String(rawValue || '').trim();
    if (!value) {
      return;
    }

    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) {
      return;
    }

    const normalized = String(number);
    const currentValues = Array.isArray(form.leverages) ? form.leverages.map((item) => String(item)) : [];
    if (currentValues.includes(normalized)) {
      return;
    }

    onFormChange('leverages', [...currentValues, normalized]);
  }, [form.leverages, onFormChange]);

  const onLeverageTagDelete = useCallback((tagIndex) => {
    const currentValues = Array.isArray(form.leverages) ? form.leverages.map((item) => String(item)) : [];
    onFormChange('leverages', currentValues.filter((_, index) => index !== tagIndex));
  }, [form.leverages, onFormChange]);

  const onLeverageTagAdd = useCallback((newTag) => {
    const rawValue = typeof newTag?.value === 'string' && newTag.value.trim().length > 0
      ? newTag.value
      : (typeof newTag?.label === 'string' ? newTag.label : '');
    addLeverageTagValue(rawValue);
  }, [addLeverageTagValue]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();

    const payload = buildTradingAccountPayloadFromForm(form);
    const nextErrors = validateTradingAccountPayload(payload, form.capabilities);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setModalError('Please fix validation errors.');
      return;
    }

    setIsSaving(true);
    setModalError('');
    setSuccess('');

      try {
        if (editingTargetId) {
          const response = await adminApi.updateTradingAccount(token, editingTargetId, payload);
          const updatedDto = mapTradingAccountItemToDto({
            id: editingTargetId,
            ...(response?.account || payload),
          });
          const updated = buildTradingAccountsRowsModel([updatedDto])[0];
          setRows((prev) => prev.map((row) => (row.id === editingTargetId ? { ...updated, id: editingTargetId } : row)));
          setEditingTargetId('');
          setFormErrors({});
          setModalError('');
          setSuccess('Trading account updated.');
        } else {
          const response = await adminApi.createTradingAccount(token, payload);
          const createdDto = mapTradingAccountItemToDto(response?.account || payload);
          const created = buildTradingAccountsRowsModel([createdDto])[0];
          setRows((prev) => [...prev.filter(Boolean), created]);
          setIsCreateOpen(false);
          setFormErrors({});
          setModalError('');
          setSuccess('Trading account created.');
      }
    } catch (requestError) {
      setFormErrors(mapTradingAccountFieldErrors(requestError));
      setModalError(requestError.message || 'Failed to save trading account');
    } finally {
      setIsSaving(false);
    }
  }, [editingTargetId, form, token]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.deleteTradingAccount(token, deleteTargetId);
      setRows((prev) => prev.filter((row) => row.id !== deleteTargetId));
      setDeleteTargetId('');
      setSuccess('Trading account deleted.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to delete trading account');
    } finally {
      setIsSaving(false);
    }
  }, [deleteTargetId, token]);

  return {
    rows,
    platformOptions,
    currencyOptions,
    tradingServerOptions,
    isLoading,
    isSaving,
    error,
    success,
    detailsRow,
    serverLabelById,
    isCreateOpen,
    editingTargetId,
    deleteTargetId,
    form,
    formErrors,
    modalError,
    leverageInput,
    setLeverageInput,
    closeModals,
    openCreate,
    openEdit,
    openDetails,
    requestDelete,
    onFormChange,
    filteredTradingServers,
    leverageTags,
    addLeverageTagValue,
    onLeverageTagDelete,
    onLeverageTagAdd,
    saveForm,
    confirmDelete,
  };
}
